import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { socioApi } from "@/api/client";
import { importPrivateKey, InvalidPrivateKeyError } from "@/keystore/import";
import { PasswordInput } from "@/components/PasswordInput";

/**
 * Importa uma owner key existente neste device.
 *
 * Cenários típicos:
 *  - Trocou de notebook → cola a chave exportada do antigo
 *  - Mudou de URL (Vite vs Caddy) e o IndexedDB ficou em outra origem
 *  - Limpou dados do navegador sem querer
 *
 * A chave importada é criptografada com sua senha (mesma que do login)
 * e salva no IndexedDB. Depois validamos contra o `smartAccount` que o
 * backend tem registrado pra detectar chave errada antes do usuário usar.
 */
export default function Import() {
  const navigate = useNavigate();
  const [privateKey, setPrivateKey] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { address: imported } = await importPrivateKey(privateKey, password);

      // Confere se a chave importada corresponde à smart wallet do sócio.
      let me;
      try {
        me = await socioApi.me();
      } catch {
        // /me só funciona com sessão válida — se não conseguir, segue
        // mesmo assim, o usuário pode ter chegado aqui sem login completo.
      }

      if (me?.smartAccount && me.smartAccount.toLowerCase() !== imported.toLowerCase()) {
        setError(
          `Chave importada (${imported}) não corresponde à smart wallet do sócio ` +
          `(${me.smartAccount}). Confira que pegou a chave certa do backup.`,
        );
        setLoading(false);
        return;
      }

      navigate("/dashboard");
    } catch (err: any) {
      if (err instanceof InvalidPrivateKeyError) {
        setError(err.message);
      } else {
        setError(err.message ?? "Erro inesperado");
      }
      setLoading(false);
    }
  }

  return (
    <div className="max-w-md mx-auto mt-16 p-6 border rounded-lg shadow-sm">
      <h1 className="text-2xl font-bold mb-2">Importar chave</h1>
      <p className="text-sm text-gray-600 mb-4">
        Cole a private key que você exportou de outro device (ou da MetaMask).
        Ela vai ser criptografada com sua senha e salva só neste navegador.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium">Private key</label>
          <input type="text" required value={privateKey} onChange={(e) => setPrivateKey(e.target.value)}
                 className="w-full border rounded px-3 py-2 mt-1 font-mono text-xs"
                 placeholder="0x... (64 dígitos hex)"
                 autoComplete="off" />
        </div>
        <div>
          <label className="block text-sm font-medium">Sua senha</label>
          <PasswordInput required value={password} onChange={(e) => setPassword(e.target.value)}
                         minLength={8} autoComplete="current-password" />
        </div>
        {error && <p className="text-red-600 text-sm whitespace-pre-wrap">{error}</p>}
        <button type="submit" disabled={loading}
                className="w-full bg-black text-white rounded py-2 disabled:opacity-50">
          {loading ? "Importando..." : "Importar chave"}
        </button>
      </form>
      <p className="text-sm mt-4 text-gray-600">
        Sem backup? <Link className="underline" to="/login">Voltar ao login</Link>
      </p>
    </div>
  );
}
